import { Node } from '@tiptap/core';
import { Plugin, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const PageBreak = Node.create({
  name: 'pageBreak',
  atom: true,
  selectable: true,

  parseHTML() {
    return [{ tag: 'hr[data-page-break]', context: 'doc/ | doc/paragraph/ | doc/heading/' }];
  },

  renderHTML() {
    return ['hr', { 'data-page-break': 'true' }];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          decorations: ({ doc, selection }) => {
            if (selection.empty) return null;

            const decorations: Decoration[] = [];
            doc.nodesBetween(selection.from, selection.to, (node, pos) => {
              if (node.type.name === this.name) {
                decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: 'is-selected' }));
              }
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setPageBreak:
        () =>
        ({ state, chain }) => {
          const { empty, $from } = state.selection;
          if (!empty || $from.depth > 1) return false;

          const isPageBreakAt = (index: number) => state.doc.maybeChild(index)?.type.name === this.name;
          const isAtStart = $from.parentOffset === 0;
          const isAtEnd = $from.parentOffset === $from.parent.content.size;
          const isEmptyAfterBreak = isAtStart && isAtEnd && isPageBreakAt($from.index(0) - 1);
          const neighbourIdx = isAtStart && !isEmptyAfterBreak ? $from.index(0) - 1 : isAtEnd ? $from.index(0) + 1 : -1;
          if (isPageBreakAt(neighbourIdx)) return false;

          const pageBreak = { type: this.name };
          const insertion = isEmptyAfterBreak
            ? chain().insertContentAt($from.after(), pageBreak)
            : isAtStart
              ? chain().insertContentAt({ from: Math.max($from.pos - 1, 0), to: $from.pos }, pageBreak)
              : chain().insertContent(pageBreak);

          return insertion
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                const { $to: $end } = tr.selection;
                if ($end.nodeAfter?.isTextblock) {
                  tr.setSelection(TextSelection.create(tr.doc, $end.pos + 1));
                } else if (!$end.nodeAfter) {
                  tr.insert($end.end(), state.schema.nodes.paragraph.create());
                  tr.setSelection(TextSelection.create(tr.doc, $end.end() + 1));
                }
              }
              return true;
            })
            .run();
        },
    };
  },
});
